/**
 * Ear-training exercise definitions (FEATURES F-LEARN-05). Pure and seeded:
 * questions are generated from an RNG so tests are deterministic.
 */
import type { Rng } from "../core/random";
import { CHORD_DESCRIPTION, CHORD_INTERVALS, type ChordQuality } from "../theory/chords";
import { intervalName, midiToName } from "../theory/notes";

export type ExerciseKind = "interval" | "chord";
export const MAX_LEVEL = 4;

/** Answer ids per level (cumulative): easy, distinct sounds first. */
const INTERVAL_LEVELS: number[][] = [[4, 7, 12], [3, 5], [2, 8, 9], [1, 6, 10, 11]];
const CHORD_LEVELS: ChordQuality[][] = [["maj", "min"], ["dim", "aug"], ["maj7", "7", "m7"], ["m7b5", "dim7", "sus4", "sus2"]];

export interface Choice { id: string; label: string; }

export function choicesFor(kind: ExerciseKind, level: number): Choice[] {
  const l = Math.max(1, Math.min(MAX_LEVEL, level));
  if (kind === "interval") {
    return INTERVAL_LEVELS.slice(0, l).flat().sort((a, b) => a - b).map((s) => ({ id: String(s), label: intervalName(s) }));
  }
  return CHORD_LEVELS.slice(0, l).flat().map((q) => ({ id: q, label: CHORD_DESCRIPTION[q] }));
}

export interface Question {
  kind: ExerciseKind;
  level: number;
  answerId: string;
  /** Note groups to play in order (one group per step). */
  steps: number[][];
  /** Human description of what was played — shown after answering, derived from the notes. */
  reveal: string;
}

/** How a question is played at each level (intervals get harder by direction and simultaneity). */
export function intervalStyle(level: number): "ascending" | "descending" | "harmonic" {
  return level >= 4 ? "harmonic" : level >= 3 ? "descending" : "ascending";
}

export function makeQuestion(kind: ExerciseKind, level: number, rng: Rng, previousAnswer?: string): Question {
  const choices = choicesFor(kind, level);
  // Avoid asking the same thing twice in a row when there is a choice.
  const pool = choices.length > 1 ? choices.filter((c) => c.id !== previousAnswer) : choices;
  const answer = pool[Math.floor(rng() * pool.length)]!;
  const root = 52 + Math.floor(rng() * 16); // E3–G4: comfortable register
  if (kind === "interval") {
    const semis = Number(answer.id);
    const style = intervalStyle(level);
    const [a, b] = style === "descending" ? [root + semis, root] : [root, root + semis];
    const steps = style === "harmonic" ? [[a, b]] : [[a], [b]];
    return {
      kind, level, answerId: answer.id, steps,
      reveal: `${midiToName(a)} ${style === "harmonic" ? "+" : "→"} ${midiToName(b)}: ${intervalName(semis)} (${semis} semitone${semis === 1 ? "" : "s"}${style === "harmonic" ? ", played together" : `, ${style}`}).`,
    };
  }
  const q = answer.id as ChordQuality;
  const notes = CHORD_INTERVALS[q].map((i) => root + i);
  return {
    kind, level, answerId: q, steps: [notes],
    reveal: `${midiToName(root)} ${CHORD_DESCRIPTION[q]}: ${notes.map((n) => midiToName(n)).join(" ")}.`,
  };
}
